/**
 * TranslationManager
 *
 *  Singleton, DOM-level translator. Lives outside React so it survives
 *  route changes and source-script re-renders.
 *
 *  Design goals (in priority order):
 *
 *  1. **Text only.** We only ever mutate `Text` node `nodeValue`. We
 *     never touch attributes, classes, styles, images, or layout - so
 *     alignment, font size, colour, formatting, and imagery are
 *     guaranteed identical to the source after translation.
 *
 *  2. **Instant.** A persistent translation cache in sessionStorage
 *     means once the user has visited a language during this session,
 *     re-picking it applies all known strings synchronously, with no
 *     network round-trip.
 *
 *  3. **Progressive.** On a first-time language pick, cached strings
 *     apply immediately; uncached strings stream in with a high level
 *     of parallelism, applied per-string as soon as their translation
 *     resolves, so the page updates progressively rather than blocking
 *     on the slowest sentence.
 *
 *  4. **Self-healing.** A character-data MutationObserver re-applies
 *     translations if a source script overwrites a translated node;
 *     a childList observer translates newly added nodes (e.g. when the
 *     SPA hash-router unhides another sub-page).
 *
 *  5. **No feedback loops.** Our own writes drain queued mutations via
 *     `takeRecords()` so the observers never react to our edits.
 */

import { translateText } from './translate.js'
import { DEFAULT_LANG } from '../languages.js'
// NOTE: precomputed-translations.json (~945 KB) is intentionally NOT
// imported statically. It is loaded lazily via dynamic import() on the
// first non-English language switch (see _ensurePrecomputed), so English
// visitors never download it and it stays out of the initial bundle.

const STORAGE_LANG_KEY  = 'pb-current-lang'
const STORAGE_CACHE_KEY = 'pb-translation-cache-v1'
const RETRANSLATE_DEBOUNCE_MS = 200
const CONCURRENCY = 12

const SKIP_SELECTOR =
  'script, style, code, pre, kbd, samp, ' +
  'input, textarea, select, option, ' +
  '[contenteditable="true"], ' +
  '.pb-lang-pill, .pb-lang-menu, ' +
  // BrandProtector tags product names / wordmarks / brand links with
  // translate="no"; honour it so those stay in their original form.
  '[translate="no"], ' +
  '[data-pb-skip-translate]'

// During the standard text-node pass we don't want to write into
// `.word` spans individually - they are produced by the source's
// per-word fade-up animator out of a single sentence. We translate
// those as one block (see _applyWordGroups) and skip them here so
// they aren't double-translated word-by-word.
const SKIP_WORD_SPANS = SKIP_SELECTOR + ', .word'

const NON_TRANSLATABLE = /^[\s\d.,:;!?/|()\-–-•·©®™%$#@&*+=<>"'`~^[\]{}]+$/

export class TranslationManager {
  constructor() {
    this.originals = new WeakMap()
    this.applied   = new WeakMap()
    this.touched   = new Set()
    this.lang      = readStoredLang()
    // Seed the per-target cache from the precomputed dictionary (built
    // by `node scripts/pretranslate.mjs`). That makes every known
    // string instant - zero network. Anything the script missed (e.g.
    // copy added after the build) is still filled in at runtime via
    // `translateText` and then layered on top via _applyLanguage's
    // missing-sources pass.
    // Runtime additions persist to sessionStorage as a *delta* on top
    // of the precomputed bundle. We keep them separate so flushes
    // don't repeatedly serialize the (much larger) precomputed table.
    this.runtimeAdditions = readStoredCache()
    // Precomputed dictionary loads lazily (see _ensurePrecomputed); until
    // then the cache holds only runtime (sessionStorage) additions.
    this.precomputed = null
    this.precomputedPromise = null
    this.cache     = mergeCaches({}, this.runtimeAdditions)
    this.busy      = false
    this.progress  = { done: 0, total: 0 }
    this.listeners = new Set()
    this.charMO    = null
    this.childMO   = null
    this.suspendDepth = 0
    this.retranslateTimer = 0
    this.abortCtrl = null
    this.started  = false
    this.flushTimer = 0
  }

  // ---- public API ---------------------------------------------------
  start() {
    if (this.started) return
    this.started = true
    this._installObservers()
    if (this.lang !== DEFAULT_LANG) {
      // Re-apply persisted language as soon as the initial body is up.
      Promise.resolve().then(() => this.setLanguage(this.lang, { resume: true }))
    }
  }

  stop() {
    this.started = false
    this.charMO?.disconnect()
    this.childMO?.disconnect()
    this.charMO = this.childMO = null
    clearTimeout(this.retranslateTimer)
    clearTimeout(this.flushTimer)
    this.abortCtrl?.abort()
  }

  subscribe(fn) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getState() {
    return { lang: this.lang, busy: this.busy, progress: this.progress }
  }

  /**
   * Lazily load the ~945 KB precomputed translation dictionary the first
   * time a non-English language is needed, then merge it into the live
   * cache (runtime/sessionStorage additions keep priority). English
   * visitors never trigger this, keeping the JSON out of the initial
   * bundle. Safe to call repeatedly - the import resolves once.
   */
  _ensurePrecomputed() {
    if (this.precomputed) return Promise.resolve()
    if (!this.precomputedPromise) {
      this.precomputedPromise = import('../precomputed-translations.json')
        .then((mod) => {
          this.precomputed = mod.default || mod
          this.cache = mergeCaches(this.precomputed, this.runtimeAdditions)
        })
        .catch(() => { this.precomputed = {} })
    }
    return this.precomputedPromise
  }

  /**
   * Set the active language. Returns a Promise that resolves once
   * cached translations are applied (i.e. within a frame). Network
   * fetches for uncached strings continue streaming in afterwards.
   */
  async setLanguage(target, { resume = false } = {}) {
    if (!target) target = DEFAULT_LANG
    if (target === this.lang && !resume) {
      if (target === DEFAULT_LANG) return
    }
    this.lang = target
    writeStoredLang(target)
    this._emit()

    this.abortCtrl?.abort()
    this.abortCtrl = new AbortController()
    const signal = this.abortCtrl.signal

    if (target === DEFAULT_LANG) {
      this._revertWordGroups()
      this._revertAll()
      this.busy = false
      this.progress = { done: 0, total: 0 }
      this._emit()
      return
    }

    // Ensure the precomputed dictionary is loaded before we read the
    // cache (lazy first-load for non-English visitors). English never
    // reaches here, so it never pays this cost.
    await this._ensurePrecomputed()
    if (signal.aborted) return

    // 1) Apply everything we already have in cache - synchronously.
    const nodes = this._collectTextNodes()
    const cacheForTarget = (this.cache[target] = this.cache[target] || {})
    const missingSources = new Set()

    this._withObserversSuspended(() => {
      for (const n of nodes) {
        if (!this.originals.has(n)) {
          this.originals.set(n, n.nodeValue)
          n.__pbOriginal = n.nodeValue
        }
        const raw = this.originals.get(n)
        const core = this._norm(raw)
        // Match the exact raw string first (runtime additions), then the
        // whitespace-normalised key. Precomputed keys are trimmed and have
        // their whitespace collapsed, so a text node carrying leading/
        // trailing whitespace or source newlines (hero lead, a CTA label
        // sitting before an inline arrow) would otherwise miss the cache and
        // fall through to the flaky live translator. We preserve the node's
        // own surrounding whitespace when writing the translation back.
        let out = null
        if (typeof cacheForTarget[raw] === 'string' && cacheForTarget[raw].length > 0) {
          out = cacheForTarget[raw]
        } else if (typeof cacheForTarget[core] === 'string' && cacheForTarget[core].length > 0) {
          out = this._wrapWS(raw, cacheForTarget[core])
        }
        if (out != null) {
          if (n.nodeValue !== out) n.nodeValue = out
          this.applied.set(n, out)
          this.touched.add(n)
        } else {
          missingSources.add(core)
        }
      }
      document.documentElement.setAttribute('lang', target.split('-')[0])
    })

    // Apply per-sentence word-group translations in parallel with the
    // text-node fetch loop. We don't await here - they fire-and-write.
    this._applyWordGroups(target, signal).catch(() => {})

    if (missingSources.size === 0) {
      this.busy = false
      this.progress = { done: 0, total: 0 }
      this._emit()
      return
    }

    // 2) Fetch the missing strings, streaming-apply each as it arrives.
    this.busy = true
    this.progress = { done: 0, total: missingSources.size }
    this._emit()

    const queue = Array.from(missingSources)
    let cursor = 0
    let done = 0

    const applyOne = (src, translated) => {
      if (!translated || translated === src) return
      cacheForTarget[src] = translated
      // Record as a runtime addition so it's persisted (the precomputed
      // bundle stays read-only and is never written back to storage).
      this.runtimeAdditions[target] = this.runtimeAdditions[target] || {}
      this.runtimeAdditions[target][src] = translated
      this._withObserversSuspended(() => {
        for (const n of nodes) {
          const raw = this.originals.get(n)
          if (raw == null) continue
          // src is the whitespace-normalised key we queued; match nodes by
          // their normalised original and re-attach their own whitespace.
          if (this._norm(raw) === src) {
            const out = this._wrapWS(raw, translated)
            if (n.nodeValue !== out) {
              n.nodeValue = out
              this.applied.set(n, out)
              this.touched.add(n)
            }
          }
        }
      })
      this._scheduleCacheFlush()
    }

    const worker = async () => {
      while (cursor < queue.length) {
        if (signal.aborted) return
        const i = cursor++
        const src = queue[i]
        try {
          const t = await translateText(src, target, { source: DEFAULT_LANG, signal })
          if (!signal.aborted) applyOne(src, t)
        } catch (_) {
          // network failure - leave the original in place
        }
        done++
        this.progress = { done, total: queue.length }
        this._emit()
      }
    }

    const workers = Array.from(
      { length: Math.min(CONCURRENCY, queue.length) },
      worker
    )
    await Promise.all(workers)

    if (signal.aborted) return
    this.busy = false
    this._emit()
    this._scheduleCacheFlush(true)
  }

  // ---- internals ----------------------------------------------------

  _revertAll() {
    this._withObserversSuspended(() => {
      for (const n of this.touched) {
        const original = this.originals.get(n) ?? n.__pbOriginal
        if (original != null && n.nodeValue !== original) {
          n.nodeValue = original
        }
        this.applied.delete(n)
      }
      document.documentElement.setAttribute('lang', 'en')
    })
  }

  // Collapse whitespace + trim - matches how pretranslate.mjs normalises
  // extracted keys, so runtime lookups hit the precomputed dictionary.
  _norm(s) { return (s || '').replace(/\s+/g, ' ').trim() }

  // Re-attach a node's own leading/trailing whitespace around a translation
  // (keeps the space before an inline arrow, indentation, etc.).
  _wrapWS(raw, translated) {
    const lead = (raw.match(/^\s+/) || [''])[0]
    const trail = (raw.match(/\s+$/) || [''])[0]
    return lead + translated + trail
  }

  // Key for word-split hero sentences: normalise whitespace and drop the
  // space a per-word join leaves before punctuation.
  _wordKey(s) { return this._norm(s).replace(/\s+([.,;:!?])/g, '$1') }

  _collectTextNodes() {
    const out = []
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const v = node.nodeValue
        if (!v || !v.trim()) return NodeFilter.FILTER_REJECT
        if (NON_TRANSLATABLE.test(v)) return NodeFilter.FILTER_REJECT
        const p = node.parentElement
        if (!p) return NodeFilter.FILTER_REJECT
        if (p.closest(SKIP_WORD_SPANS)) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })
    let n
    while ((n = walker.nextNode())) out.push(n)
    return out
  }

  /**
   * The source HTML's hero animator splits `[data-hero-h1]` text into
   * `<span class="word">` per-word spans, with the flag <img> and
   * accented spans interleaved between them. Translating each word in
   * isolation loses sentence context and produces garbage. Instead we
   * group sibling `.word` spans by their nearest container, translate
   * the joined sentence in a single API call, then redistribute the
   * translated tokens back across the spans - preserving the
   * staggered fade-in animation AND the inline flag image.
   */
  async _applyWordGroups(target, signal) {
    const allWordSpans = Array.from(document.querySelectorAll('.word'))
    if (allWordSpans.length === 0) return

    // Group by nearest container. Prefer [data-hero-h1] when present
    // so all words in a multi-line hero are translated as one
    // sentence; otherwise fall back to direct parent.
    const groups = new Map()
    for (const span of allWordSpans) {
      if (span.closest('.pb-lang-pill, .pb-lang-menu, [data-pb-skip-translate]')) continue
      const key = span.closest('[data-hero-h1]') || span.parentElement
      if (!key) continue
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(span)
    }
    if (groups.size === 0) return

    for (const [, spans] of groups) {
      if (signal?.aborted) return
      // Stash the original token per span the first time we see it.
      for (const span of spans) {
        if (span.__pbWordOriginal == null) {
          span.__pbWordOriginal = span.textContent || ''
        }
      }
      const originalSentence = spans.map((s) => s.__pbWordOriginal).join(' ')
      if (!originalSentence.trim()) continue
      if (NON_TRANSLATABLE.test(originalSentence)) continue

      // Prefer a precomputed/cached translation. The key normalises
      // whitespace AND drops spaces before punctuation, so the runtime
      // word-join ("... North America .") matches the extracted sentence
      // ("... North America."). Only hit the live translator when missing,
      // then cache the result so it's instant next time.
      const key = this._wordKey(originalSentence)
      let translated = this.cache[target] && this.cache[target][key]
      if (typeof translated !== 'string' || !translated.length) {
        translated = await translateText(originalSentence, target, {
          source: DEFAULT_LANG,
          signal,
        })
        if (translated && translated !== originalSentence) {
          this.cache[target] = this.cache[target] || {}
          this.cache[target][key] = translated
          this.runtimeAdditions[target] = this.runtimeAdditions[target] || {}
          this.runtimeAdditions[target][key] = translated
          this._scheduleCacheFlush()
        }
      }
      if (!translated || translated === originalSentence) continue

      const tokens = translated.split(/\s+/).filter(Boolean)
      // Distribute tokens across the source spans proportionally so
      // the relative position of each word (and the colour/style of
      // the span it lives in, e.g. the red "North America") is
      // preserved as closely as the translation allows.
      this._withObserversSuspended(() => {
        spans.forEach((span, i) => {
          const start = Math.floor((i * tokens.length) / spans.length)
          const end = Math.floor(((i + 1) * tokens.length) / spans.length)
          const next = tokens.slice(start, end).join(' ')
          if (span.textContent !== next) {
            span.textContent = next
          }
        })
      })
    }
  }

  /**
   * Restore `.word` spans to their original tokens (used on revert).
   */
  _revertWordGroups() {
    document.querySelectorAll('.word').forEach((span) => {
      if (span.__pbWordOriginal != null && span.textContent !== span.__pbWordOriginal) {
        this._withObserversSuspended(() => {
          span.textContent = span.__pbWordOriginal
        })
      }
    })
  }

  _installObservers() {
    this.charMO = new MutationObserver((muts) => {
      if (this.suspendDepth > 0) return
      if (this.lang === DEFAULT_LANG) return
      let needsRetranslate = false
      for (const m of muts) {
        if (m.type !== 'characterData') continue
        const n = m.target
        if (!(n instanceof Text)) continue
        const want = this.applied.get(n)
        if (typeof want === 'string') {
          if (n.nodeValue !== want) {
            this._withObserversSuspended(() => { n.nodeValue = want })
          }
        } else {
          needsRetranslate = true
        }
      }
      if (needsRetranslate) this._queueRetranslate()
    })
    this.charMO.observe(document.body, { characterData: true, subtree: true })

    this.childMO = new MutationObserver((muts) => {
      if (this.suspendDepth > 0) return
      if (this.lang === DEFAULT_LANG) return
      let hasAdditions = false
      for (const m of muts) {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          hasAdditions = true
          break
        }
      }
      if (hasAdditions) this._queueRetranslate()
    })
    this.childMO.observe(document.body, { childList: true, subtree: true })
  }

  _queueRetranslate() {
    if (!this.started || this.lang === DEFAULT_LANG) return
    clearTimeout(this.retranslateTimer)
    this.retranslateTimer = setTimeout(() => {
      this.setLanguage(this.lang, { resume: true })
    }, RETRANSLATE_DEBOUNCE_MS)
  }

  _withObserversSuspended(fn) {
    this.suspendDepth++
    try {
      fn()
    } finally {
      this.charMO?.takeRecords()
      this.childMO?.takeRecords()
      this.suspendDepth--
    }
  }

  _scheduleCacheFlush(immediate = false) {
    if (immediate) {
      writeStoredCache(this.runtimeAdditions)
      return
    }
    clearTimeout(this.flushTimer)
    this.flushTimer = setTimeout(
      () => writeStoredCache(this.runtimeAdditions),
      400
    )
  }

  _emit() {
    const s = this.getState()
    for (const fn of this.listeners) fn(s)
  }
}

// ---- session storage helpers ---------------------------------------
function readStoredLang() {
  try { return sessionStorage.getItem(STORAGE_LANG_KEY) || DEFAULT_LANG }
  catch (_) { return DEFAULT_LANG }
}
function writeStoredLang(v) {
  try { sessionStorage.setItem(STORAGE_LANG_KEY, v) } catch (_) {}
}
function readStoredCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_CACHE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (_) { return {} }
}
/**
 * Layer two caches together. Runtime-stored entries take priority over
 * precomputed (so manual fixes survive a build).
 */
function mergeCaches(precomputed, runtime) {
  const out = {}
  for (const lang of new Set([...Object.keys(precomputed || {}), ...Object.keys(runtime || {})])) {
    out[lang] = { ...(precomputed?.[lang] || {}), ...(runtime?.[lang] || {}) }
  }
  return out
}

function writeStoredCache(cache) {
  try { sessionStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(cache)) }
  catch (_) {
    // Storage full - drop the oldest language bucket and retry once.
    try {
      const langs = Object.keys(cache)
      if (langs.length > 1) {
        delete cache[langs[0]]
        sessionStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(cache))
      }
    } catch (_) {}
  }
}

let _instance = null
export function getTranslationManager() {
  if (!_instance) _instance = new TranslationManager()
  return _instance
}
